/**
 * Single-instance modal that announces a pending service-worker update and
 * waits for the user to confirm a page refresh. Built on top of the shared
 * `Modal` primitive — focus-trapped, dismissible by Escape, primary button
 * focused on open.
 *
 * Single-instance dedup is defensive: in practice the SW only fires
 * `onNeedRefresh` once per update, but we don't want to stack modals if it
 * fires twice. The second caller gets the existing handle back; its options
 * are discarded.
 */

import { openModal, type ModalHandle } from "./components/Modal";

export interface UpdatePromptOptions {
  readonly title: string;
  readonly body: string;
  readonly refreshLabel: string;
  readonly dismissLabel: string;
  readonly onConfirm: () => void;
  readonly onDismiss?: () => void;
}

export interface UpdatePromptHandle {
  /** Close the modal without invoking either callback. */
  readonly dispose: () => void;
}

let activeHandle: UpdatePromptHandle | null = null;

export function createUpdatePrompt(opts: UpdatePromptOptions): UpdatePromptHandle {
  if (activeHandle !== null) return activeHandle;

  let settled = false;
  let modal: ModalHandle;

  function settle(action: "confirm" | "dismiss" | "dispose"): void {
    if (settled) return;
    settled = true;
    document.removeEventListener("keydown", onKeydown);
    modal.close();
    activeHandle = null;
    if (action === "confirm") opts.onConfirm();
    else if (action === "dismiss") opts.onDismiss?.();
  }

  function onKeydown(e: KeyboardEvent): void {
    if (e.key === "Escape") {
      e.preventDefault();
      settle("dismiss");
    }
  }

  document.addEventListener("keydown", onKeydown);

  // dismissible: false closes the overlay-click escape hatch — every exit
  // path (Refresh / Later / Escape / dispose) must route through settle()
  // so activeHandle is reset and future SW updates aren't silently no-op'd.
  modal = openModal({
    title: opts.title,
    body: opts.body,
    dismissible: false,
    actions: [
      { label: opts.dismissLabel, onClick: () => settle("dismiss") },
      { label: opts.refreshLabel, primary: true, onClick: () => settle("confirm") },
    ],
  });

  queueMicrotask(() => modal.focusPrimary());

  const handle: UpdatePromptHandle = {
    dispose: () => settle("dispose"),
  };
  activeHandle = handle;
  return handle;
}
